import JSZip from "jszip";
import { kml as kmlToGeoJson } from "@tmcw/togeojson";
import type { FeatureCollection, Geometry } from "geojson";
import type { ApiClient } from "../api/client";

export interface LoadedOverlay {
  id: string;
  name: string;
  fc: FeatureCollection<Geometry | null>;
  /** A sub-client's view of its parent's overlay: shown, never removable here. */
  inherited?: true;
}

/** Unzip + parse KMZ bytes to GeoJSON. Returns null on corrupt/empty. Never throws. */
export async function parseKmz(
  bytes: ArrayBuffer,
): Promise<FeatureCollection<Geometry | null> | null> {
  try {
    const zip = await JSZip.loadAsync(bytes);
    const entry = Object.values(zip.files).find((f) =>
      f.name.toLowerCase().endsWith(".kml"),
    );
    if (entry === undefined) return null;
    const xml = await entry.async("text");
    const doc = new DOMParser().parseFromString(xml, "text/xml");
    return kmlToGeoJson(doc);
  } catch (err) {
    console.error("overlay parse failed", err);
    return null;
  }
}

/**
 * List the team's overlays and parse each KMZ. Overlays that fail to fetch or
 * parse are dropped.
 *
 * A *list* failure rejects rather than returning []: only the caller can tell
 * "the Worker is unreachable" apart from "this team has no overlays", and
 * ViewerPage answers the first from the offline cache (spec §6.1). It catches,
 * so a backend hiccup still never blocks the viewer.
 */
export async function loadOverlays(
  client: ApiClient,
  team?: string,
): Promise<LoadedOverlay[]> {
  const list = (await client.listOverlays(team)).overlays;
  const loaded: LoadedOverlay[] = [];
  for (const { id, name, inherited } of list) {
    let bytes: ArrayBuffer | null;
    try {
      bytes = await client.getOverlayBytes(id, team);
    } catch (err) {
      console.error("overlay fetch failed", id, err);
      continue;
    }
    if (bytes === null) continue;
    const fc = await parseKmz(bytes);
    if (fc === null) continue;
    loaded.push(inherited === true ? { id, name, fc, inherited } : { id, name, fc });
  }
  return loaded;
}
